import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

/**
 * Real-time updates for inbox via ActionCable
 *
 * Subscribes to InboxChannel and handles:
 * - New conversations
 * - New messages (updates preview)
 * - Conversation status changes
 * - Unread count updates
 */
export default class extends Controller {
  static values = {
    organizationId: Number
  };

  connect() {
    if (!this.organizationIdValue) {
      console.warn("InboxRealtimeController: No organization ID");
      return;
    }

    // Prevent duplicate subscriptions
    if (this.subscription) {
      return;
    }

    this.subscription = consumer.subscriptions.create(
      {
        channel: "InboxChannel",
        organization_id: this.organizationIdValue
      },
      {
        received: (data) => this.handleReceived(data)
      }
    );
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  // Check if we're still on an inbox page (not navigated away via Turbo)
  isOnInboxPage() {
    return window.location.pathname.includes("/inbox") ||
           this.element.isConnected;
  }

  handleReceived(data) {
    switch (data.type) {
    case "new_conversation":
      this.handleNewConversation(data);
      break;
    case "new_message":
      this.handleNewMessage(data);
      break;
    case "conversation_updated":
      this.handleConversationUpdated(data);
      break;
    case "unread_counts":
      this.handleUnreadCounts(data);
      break;
    }
  }

  handleNewConversation(_data) {
    // Only reload if we're still on the inbox page
    if (!this.isOnInboxPage()) {
      return;
    }
    // Reload the page to show new conversation
    // Could be enhanced to prepend to list via Turbo Stream
    Turbo.visit(window.location.href, { action: "replace" });
  }

  handleNewMessage(data) {
    // Only handle if we're still on the inbox page
    if (!this.isOnInboxPage()) {
      return;
    }

    const conversationItem = this.element.querySelector(
      `[data-conversation-id="${data.conversation_id}"]`
    );

    if (conversationItem) {
      // Update the preview text
      const preview = conversationItem.querySelector("p.text-gray-500, p.dark\\:text-gray-400");
      if (preview && data.preview) {
        preview.textContent = data.preview;
      }

      // Update unread badge
      this.updateConversationUnreadBadge(conversationItem, data.unread_count);

      // Move to top of list (most recent first)
      const list = conversationItem.closest("ul");
      if (list && list.firstElementChild !== conversationItem) {
        list.prepend(conversationItem);
        this.reindexItems();
      }
    } else {
      // New conversation not in view, reload
      Turbo.visit(window.location.href, { action: "replace" });
    }
  }

  handleConversationUpdated(data) {
    const assignmentFilter = new URLSearchParams(window.location.search).get("assigned");
    if (assignmentFilter) {
      Turbo.visit(window.location.href, { action: "replace" });
      return;
    }

    const conversationItem = this.element.querySelector(
      `[data-conversation-id="${data.conversation_id}"]`
    );

    if (conversationItem) {
      // Update status badge
      const statusBadge = conversationItem.querySelector(".capitalize");
      if (statusBadge) {
        statusBadge.textContent = data.status;
        // Update status colors
        statusBadge.className = statusBadge.className.replace(
          /bg-\w+-100 text-\w+-800|bg-\w+-900\/30 dark:text-\w+-300/g,
          ""
        );
        const colorClass = this.getStatusColorClass(data.status);
        statusBadge.classList.add(...colorClass.split(" "));
      }

      // Update assignee display
      this.updateAssigneeDisplay(data.conversation_id, data.assignee_id, data.assignee_name);

      // Update unread badge
      this.updateConversationUnreadBadge(conversationItem, data.unread_count);
    }
  }

  updateAssigneeDisplay(conversationId, assigneeId, assigneeName) {
    const assigneeSpan = this.element.querySelector(
      `[data-assignee-for="${conversationId}"]`
    );

    if (assigneeSpan) {
      if (assigneeId && assigneeName) {
        assigneeSpan.textContent = assigneeName;
        assigneeSpan.className = "text-xs text-gray-500 dark:text-gray-400";
      } else {
        assigneeSpan.textContent = "Unassigned";
        assigneeSpan.className = "text-xs font-medium text-amber-700 dark:text-amber-300";
      }
    }
  }

  handleUnreadCounts(data) {
    // Update tab badges
    const tabs = this.element.querySelectorAll("nav a");

    tabs.forEach(tab => {
      const href = tab.getAttribute("href");
      let channel = "all";

      if (href.includes("channel=chat")) channel = "chat";
      else if (href.includes("channel=sms")) channel = "sms";
      else if (href.includes("channel=email")) channel = "email";

      const count = channel === "all" ? data.total : (data.counts[channel] || 0);
      const badge = tab.querySelector("span");

      if (count > 0) {
        if (badge) {
          badge.textContent = count;
        } else {
          const newBadge = document.createElement("span");
          newBadge.className = "ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300";
          newBadge.textContent = count;
          tab.appendChild(newBadge);
        }
      } else if (badge) {
        badge.remove();
      }
    });

    // Update header subtitle
    const subtitle = this.element.querySelector("p.text-gray-500");
    if (subtitle && data.total > 0) {
      const plural = data.total === 1 ? "message" : "messages";
      subtitle.textContent = `${data.total} unread ${plural} across all channels`;
    } else if (subtitle) {
      subtitle.textContent = "";
    }
  }

  updateConversationUnreadBadge(item, count) {
    const badge = item.querySelector(".rounded-full.bg-indigo-100");

    if (count > 0) {
      if (badge) {
        badge.textContent = count;
      } else {
        const container = item.querySelector(".flex.items-center.ml-4");
        if (container) {
          const newBadge = document.createElement("span");
          newBadge.className = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300";
          newBadge.textContent = count;
          container.prepend(newBadge);
        }
      }
    } else if (badge) {
      badge.remove();
    }
  }

  getStatusColorClass(status) {
    switch (status) {
    case "open":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    case "pending":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  }

  reindexItems() {
    const items = this.element.querySelectorAll("[data-index]");
    items.forEach((item, index) => {
      item.dataset.index = index;
    });
  }
}
